import CategoryCard from '../category-card/CategoryCard';
import getUrlPath from '../../../utils/getUrlPath';
import Loader from '../../../components/Loader';
import useCategories from '../../../hooks/useCategories';
import { Category } from '../../../types/categories';
import { FormattedMessage } from 'react-intl';
import { PaginationDetails, Root, SeeMore, SeeMoreContainer } from './CategoriesStyle';
import { url } from '../../../constants/url';
import { useEffect, useState } from 'react';
import {
  sortCategoriesByPopularityAcrossCategory,
  sortCategoriesByPopularityInCategory,
} from '../../../utils/categories';

const DEFAULT_PAGE_SIZE = 4;
const DEFAULT_NUMBER_OF_SUBCATEGORIES = 1;

const getNumberOfSubcategories = (value: number) => {
  return value > 0 ? value : DEFAULT_NUMBER_OF_SUBCATEGORIES;
};

/*
 * PageSize Behavior:
 * - If set to a negative number, all remaining items are loaded on "show more" function.
 * - If set to 0, the DEFAULT_PAGE_SIZE value is used.
 * - For any positive number, the passed value is used as the page size.
 */
interface CategoriesProps {
  pageSize: number;
  pageOffset: number;
  numberOfSubcategories: number;
}

function Categories({ pageSize, pageOffset, numberOfSubcategories }: CategoriesProps) {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentCount, setCurrentCount] = useState<number>(pageOffset);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [displayedCategories, setDisplayedCategories] = useState<Category[]>([]);
  const [numberOfSubCategories] = useState<number>(() =>
    getNumberOfSubcategories(numberOfSubcategories),
  );

  const { categories, isCategoriesLoading } = useCategories();

  const seeMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  useEffect(() => {
    const size =
      Number(pageSize) < 0
        ? totalCount
        : Number(pageSize) === 0
        ? DEFAULT_PAGE_SIZE
        : Number(pageSize);

    const nextCount = Math.min(currentPage * size, totalCount);
    setCurrentCount(nextCount);
  }, [currentPage]);

  useEffect(() => {
    if (!categories) {
      return;
    }
    const nextList = categories
      ?.slice()
      .sort(sortCategoriesByPopularityAcrossCategory)
      .slice(pageOffset, currentCount);
    setDisplayedCategories(nextList);
  }, [currentCount]);

  useEffect(() => {
    if (!categories) {
      return;
    }
    setCurrentCount(pageOffset < categories.length ? pageOffset : categories.length);
    setTotalCount(categories.length);
    if (!isInitialised) {
      setIsInitialised(true);
    }
  }, [categories]);

  return (
    <Root>
      {displayedCategories.map((category, categoryIndex) => (
        <div key={categoryIndex} className='category-container'>
          <h2 className='category-title'>{category.name}</h2>
          <div className='sub-category-container'>
            {Array.isArray(category.subCategories) &&
              category.subCategories
                .slice()
                .sort(sortCategoriesByPopularityInCategory)
                .slice(0, numberOfSubCategories)
                .map((subCategory, subCategoryIndex) => (
                  <CategoryCard key={subCategoryIndex} slug={subCategory.slug} />
                ))}
          </div>
          <a
            className='category-link'
            href={`${getUrlPath(url.rental.subPath.categories)}/${category.slug}`}
          >
            <div className='category-link-text'>
              <span className='category-link-text-span see-all'>
                <FormattedMessage id='categories.seeAll' />
              </span>
              <span className='category-link-text-span'>{category.name}</span>
            </div>
            <img
              src='https://cdn.prod.website-files.com/64e3b59c648b525b252eacf6/6710aed4885634bf14d0a5bb_right-arrow-square.svg'
              loading='lazy'
              alt='arrow'
              className='category-link-icon'
            />
          </a>
        </div>
      ))}
      <SeeMoreContainer>
        {isCategoriesLoading || !isInitialised ? (
          <Loader />
        ) : (
          <>
            {currentCount < totalCount && (
              <SeeMore variant='outlined' onClick={seeMore}>
                <FormattedMessage id='categories.seeMore' />
              </SeeMore>
            )}

            <PaginationDetails>
              <FormattedMessage id='categories.showing' />
              {` ${currentCount} `}
              <FormattedMessage id='categories.of' />
              {` ${totalCount}`}
            </PaginationDetails>
          </>
        )}
      </SeeMoreContainer>
    </Root>
  );
}

export default Categories;
